import { useEffect, useState, type FC } from "react";
import { Button, DatePicker, Input, Modal, SelectPicker } from "rsuite";
import {
  MovementType,
  StockMovement,
} from "../../../models/inventory/stock_movement";
import { Warehouse } from "../../../models/inventory/warehouse";
import { getWarehouses } from "../../../services/inventory/WarehouseApi";
import { Product } from "../../../models/inventory/product";
import {
  getProduct,
  getProducts,
  getProductStock,
} from "../../../services/inventory/ProductApi";
import CurrencyInput from "react-currency-input-field";
import moment from "moment";
import { money } from "../../../utils/helper";

interface StockMovementFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: StockMovement) => void;
  stockMovement?: StockMovement;
}

const StockMovementForm: FC<StockMovementFormProps> = ({
  open,
  onClose,
  onSubmit,
  stockMovement,
}) => {
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [stockIn, setStockIn] = useState(0);
  const [stockOut, setStockOut] = useState(0);
  const [currentStock, setCurrentStock] = useState(0);
  const [adjustStock, setAdjustStock] = useState(0);
  const [data, setData] = useState<StockMovement>({
    id: "",
    date: new Date(),
    description: "",
    product_id: "",
    warehouse_id: "",
    quantity: 0,
    type: MovementType.MovementTypeIn,
  });
  useEffect(() => {
    if (stockMovement) {
      stockMovement.date = moment(`${stockMovement.date}`).toDate();
      if (stockMovement.type === MovementType.MovementTypeIn) {
        setStockIn(stockMovement.quantity);
        setStockOut(0);
      } else if (stockMovement.type === MovementType.MovementTypeOut) {
        setStockOut(stockMovement.quantity);
        setStockIn(0);
      }
      setData({ ...stockMovement });
    } else {
      setData({
        id: "",
        date: new Date(),
        description: "",
        product_id: "",
        warehouse_id: "",
        quantity: 0,
        type: MovementType.MovementTypeIn,
      });
    }
    getWarehouses({
      page: 1,
      size: 100,
      search: "",
    })
      .then((v) => v.json())
      .then((v) => setWarehouses(v.data.items));
    getProducts({
      page: 1,
      size: 10,
      search: "",
    })
      .then((v) => v.json())
      .then((v) => setProducts(v.data.items));
  }, [stockMovement]);

  useEffect(() => {
    setData({
      ...data,
      quantity: stockIn - stockOut,
    });
  }, [stockIn, stockOut]);

  useEffect(() => {
    if (data.type === MovementType.MovementTypeAdjust) {
      if (data.product_id && data.warehouse_id) {
        getProductStock(data.product_id, data.warehouse_id)
          .then((v) => v.json())
          .then((v) => setCurrentStock(v.data));
      }
    }
  }, [data.type, data.product_id, data.warehouse_id]);
  return (
    <Modal
      open={open}
      onClose={() => {
        setData({
          id: "",
          date: new Date(),
          description: "",
          product_id: "",
          warehouse_id: "",
          quantity: 0,
          type: MovementType.MovementTypeIn,
        });
        onClose();
      }}
    >
      <Modal.Header>
        <Modal.Title>Stock Movement</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="form-group">
          <label> Date</label>
          <div className="input">
            <DatePicker
              format="dd MMMM yyyy"
              value={data.date}
              onChange={(value) => {
                setData({ ...data, date: value! });
              }}
              block
            />
          </div>
        </div>
        <div className="form-group">
          <label> Type</label>
          <div className="input">
            <SelectPicker
              data={[
                { label: "IN", value: MovementType.MovementTypeIn },
                { label: "OUT", value: MovementType.MovementTypeOut },
                { label: "TRANSFER", value: MovementType.MovementTypeTransfer },
                { label: "ADJUST", value: MovementType.MovementTypeAdjust },
              ]}
              value={data.type}
              onChange={(value) => {
                setData({ ...data, type: value! });
              }}
              block
              onSearch={(value) => {
                if (!value) return;
                getProducts({ page: 1, size: 10, search: value! })
                  .then((v) => v.json())
                  .then((v) => {
                    setProducts(v.data.items);
                  });
              }}
            />
          </div>
        </div>
        {data.type == MovementType.MovementTypeTransfer && (
          <div className="form-group">
            <label> Source Warehouse</label>
            <div className="input">
              <SelectPicker
                data={warehouses}
                value={data.source_warehouse_id}
                onChange={(value) => {
                  setData({ ...data, source_warehouse_id: value! });
                }}
                block
                labelKey="name"
                valueKey="id"
              />
            </div>
          </div>
        )}
        <div className="form-group">
          <label> Warehouse</label>
          <div className="input">
            <SelectPicker
              data={warehouses}
              value={data.warehouse_id}
              onChange={(value) => {
                setData({ ...data, warehouse_id: value! });
              }}
              block
              labelKey="name"
              valueKey="id"
            />
          </div>
        </div>
        <div className="form-group">
          <label> Product</label>
          <div className="input">
            <SelectPicker
              data={products}
              value={data.product_id}
              onChange={(value) => {
                setData({ ...data, product_id: value! });
              }}
              block
              labelKey="name"
              valueKey="id"
              onSearch={(value) => {
                if (!value) return;
                getProducts({ page: 1, size: 10, search: value! })
                  .then((v) => v.json())
                  .then((v) => {
                    setProducts(v.data.items);
                  });
              }}
            />
          </div>
        </div>
        {data.type === MovementType.MovementTypeIn && (
          <div className="form-group">
            <label>Stock In</label>
            <div className="input">
              <CurrencyInput
                className="rs-input"
                value={stockIn}
                groupSeparator="."
                decimalSeparator=","
                onValueChange={(value, name, values) =>
                  setStockIn(values?.float || 0)
                }
              />
            </div>
          </div>
        )}
        {data.type === MovementType.MovementTypeOut && (
          <div className="form-group">
            <label>Stock Out</label>
            <div className="input">
              <CurrencyInput
                className="rs-input"
                value={stockOut}
                groupSeparator="."
                decimalSeparator=","
                onValueChange={(value, name, values) =>
                  setStockOut(values?.float || 0)
                }
              />
            </div>
          </div>
        )}
        {data.type == MovementType.MovementTypeTransfer && (
          <div className="form-group">
            <label>Quantity</label>
            <div className="input">
              <CurrencyInput
                className="rs-input"
                value={data.quantity}
                groupSeparator="."
                decimalSeparator=","
                onValueChange={(value, name, values) =>
                  setData({ ...data, quantity: values?.float || 0 })
                }
              />
            </div>
          </div>
        )}
        {data.type == MovementType.MovementTypeAdjust && (
          <>
            <div className="form-group">
              <label>Current Stock</label>
              <div className="input ">
                <div className="rs-input !bg-gray-50">{money(currentStock)}</div>
              </div>
            </div>
            <div className="form-group">
              <label>Final Stock</label>
              <div className="input">
                <CurrencyInput
                  className="rs-input"
                  value={adjustStock}
                  groupSeparator="."
                  decimalSeparator=","
                  onValueChange={(value, name, values) =>
                    setAdjustStock(values?.float || 0)
                  }
                />
              </div>
            </div>
            <div className="form-group">
              <label> Stock Movement</label>
              <div className="input ">
                <div className="rs-input !bg-gray-50">{money(adjustStock - currentStock)}</div>
              </div>
            </div>
          </>
        )}
        <div className="form-group">
          <label>Description</label>
          <Input
            as={"textarea"}
            rows={5}
            name="description"
            placeholder="Description"
            value={data.description ?? ""}
            onChange={(value, event) =>
              setData({ ...data, description: value })
            }
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="pt-4">
          <Button
            appearance="primary"
            onClick={() => {
              if (data.type == MovementType.MovementTypeAdjust) {
                data.quantity = adjustStock - currentStock;
              }
              onSubmit(data!);
              setData({
                id: "",
                date: new Date(),
                description: "",
                product_id: "",
                warehouse_id: "",
                quantity: 0,
                type: MovementType.MovementTypeIn,
              });
            }}
          >
            Submit
          </Button>
          <Button appearance="link" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
export default StockMovementForm;
